import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  MessageSquare,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { userManagementAPI } from '../../services/api/adminApi';
import type { Conversation, Message as MessageType } from '../../types/admin';
import clsx from 'clsx';

interface UserChatsViewProps {
  userId: number;
  userName: string;
  onBack: () => void;
}

export const UserChatsView: React.FC<UserChatsViewProps> = ({ userId, userName, onBack }) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadConversations();
  }, [userId]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await userManagementAPI.getUserConversations(userId);
      setConversations(response.data.items);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setLoading(true);
    try {
      const response = await userManagementAPI.getConversationMessages(conversation.id);
      setMessages(response.data.items);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleConversation = (conversationId: number) => {
    setExpandedConversations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className='bg-white min-h-full'>
      {/* Header */}
      <div className='border-b border-gray-200 px-4 sm:px-6 py-4'>
        <button
          onClick={onBack}
          className='flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors'
        >
          <ArrowLeft size={20} className='mr-2' />
          {t('admin.chats.backToList')}
        </button>
        <div className='flex items-center space-x-3'>
          <div className='w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center'>
            <UserIcon size={20} className='text-gray-500' />
          </div>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>{userName}</h2>
            <p className='text-sm text-gray-600'>
              {t('admin.chats.totalConversations', { count: conversations.length })}
            </p>
          </div>
        </div>
      </div>

      <div className='p-4 sm:p-6'>
        {/* Conversations List */}
        <div className='space-y-4'>
          <h3 className='text-lg font-semibold text-gray-900'>{t('admin.chats.conversations')}</h3>

          {loading && conversations.length === 0 ? (
            <div className='text-center py-8'>
              <div className='animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full'></div>
              <p className='mt-2 text-gray-600'>{t('common.loading')}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className='text-center py-8 bg-gray-50 rounded-lg'>
              <MessageSquare className='mx-auto h-12 w-12 text-gray-400' />
              <h3 className='mt-2 text-sm font-medium text-gray-900'>
                {t('admin.chats.noConversations')}
              </h3>
              <p className='mt-1 text-sm text-gray-500'>{t('admin.chats.noConversationsDesc')}</p>
            </div>
          ) : (
            <div className='space-y-3 max-h-[600px] overflow-y-auto pr-2'>
              {conversations.map(conversation => (
                <div
                  key={conversation.id}
                  className='border border-gray-200 rounded-lg overflow-hidden'
                >
                  {/* Conversation Header */}
                  <button
                    onClick={() => {
                      toggleConversation(conversation.id);
                      if (!expandedConversations.has(conversation.id)) {
                        loadMessages(conversation);
                      }
                    }}
                    className='w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors'
                  >
                    <div className='flex items-center space-x-3 flex-1 text-left'>
                      {expandedConversations.has(conversation.id) ? (
                        <ChevronDown size={16} className='text-gray-400 flex-shrink-0' />
                      ) : (
                        <ChevronRight size={16} className='text-gray-400 flex-shrink-0' />
                      )}
                      <MessageSquare size={18} className='text-blue-600 flex-shrink-0' />
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-gray-900 truncate'>
                          {conversation.title}
                        </p>
                        <p className='text-xs text-gray-500'>
                          {conversation.message_count} {t('admin.chats.messages')} •{' '}
                          {formatDate(conversation.updated_at)}
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Messages */}
                  {expandedConversations.has(conversation.id) && (
                    <div className='border-t border-gray-200 bg-gray-50'>
                      {loading && selectedConversation?.id === conversation.id ? (
                        <div className='p-4 text-center'>
                          <div className='animate-spin inline-block w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full'></div>
                        </div>
                      ) : (
                        <div className='p-4 space-y-3'>
                          {messages.map(message => (
                            <div
                              key={message.id}
                              className={clsx(
                                'flex',
                                message.role === 'user' ? 'justify-start' : 'justify-end'
                              )}
                            >
                              <div
                                className={clsx(
                                  'max-w-[80%] rounded-lg px-4 py-2',
                                  message.role === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-900'
                                )}
                              >
                                <div className='flex items-center space-x-2 mb-1'>
                                  <span className='text-xs font-medium'>
                                    {message.role === 'user' ? userName : 'AI'}
                                  </span>
                                  {message.model && (
                                    <span className='text-xs opacity-75'>({message.model})</span>
                                  )}
                                </div>
                                <p className='text-sm whitespace-pre-wrap break-words'>
                                  {message.content}
                                </p>
                                <div className='flex items-center justify-between mt-1'>
                                  <span className='text-xs opacity-75'>
                                    {formatDate(message.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {messages.length === 0 && (
                            <p className='text-center text-sm text-gray-500 py-4'>
                              {t('admin.chats.noMessages')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserChatsView;
